import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CircleDot, Waves, Radio } from 'lucide-react';
import ManufacturingSection from '../components/ManufacturingSection';

const TechnologyPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">
              Technology
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Sensing Made
              <br />
              <span className="text-gray-500">Simple</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              ClearWay uses proven sensor technology to detect obstacles and surface 
              hazards — providing you with the awareness you need to navigate safely.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Manufacturing Section - NOW FIRST */}
      <ManufacturingSection />

      {/* How It Works Visualization */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-blue-500/5 to-transparent border border-gray-800/50 p-8 md:p-16"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Visualization */}
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Stick representation */}
                  <div className="relative">
                    <div className="w-4 h-64 bg-gradient-to-b from-gray-600 to-gray-800 rounded-full" />
                    {/* Forward sensor indicator */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-blue-500/30 border border-blue-400/50 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                    </div>
                    {/* Down sensor indicator */}
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-purple-500/30 border border-purple-400/50 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* Forward detection arcs */}
                <svg className="absolute top-8 left-1/2 -translate-x-1/2 w-full h-32" viewBox="0 0 300 100">
                  <defs>
                    <linearGradient id="forwardGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(59, 130, 246, 0)" />
                      <stop offset="50%" stopColor="rgba(59, 130, 246, 0.5)" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
                    </linearGradient>
                  </defs>
                  <path d="M 50 90 Q 150 10 250 90" fill="none" stroke="url(#forwardGrad)" strokeWidth="1.5" className="sensor-arc" />
                  <path d="M 80 90 Q 150 30 220 90" fill="none" stroke="url(#forwardGrad)" strokeWidth="1" className="sensor-arc-delayed" />
                  <path d="M 100 90 Q 150 45 200 90" fill="none" stroke="url(#forwardGrad)" strokeWidth="0.5" className="sensor-arc-delayed-2" />
                </svg>

                {/* Downward detection beam */}
                <svg className="absolute bottom-16 left-1/2 -translate-x-1/2 w-32 h-24" viewBox="0 0 100 80">
                  <defs>
                    <linearGradient id="downGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(168, 85, 247, 0.4)" />
                      <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
                    </linearGradient>
                  </defs>
                  <polygon points="50,0 20,75 80,75" fill="url(#downGrad)" className="surface-scan" />
                  <ellipse cx="50" cy="75" rx="35" ry="5" fill="rgba(168, 85, 247, 0.2)" className="surface-scan" />
                </svg>

                {/* Labels */}
                <div className="absolute top-4 right-4 bg-gray-900/80 px-3 py-2 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-2 text-sm">
                    <CircleDot className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-300">Forward Sensor</span>
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 bg-gray-900/80 px-3 py-2 rounded-lg border border-purple-500/30">
                  <div className="flex items-center gap-2 text-sm">
                    <Waves className="w-4 h-4 text-purple-400" />
                    <span className="text-gray-300">Surface Sensor</span>
                  </div>
                </div>
              </div>

              {/* Explanation */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-semibold mb-4">Two-Zone Detection</h2>
                  <p className="text-gray-400 leading-relaxed">
                    ClearWay employs a dual-sensor approach: forward-facing sensors scan 
                    for obstacles in your path, while downward-facing sensors monitor 
                    the ground surface beneath you.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="p-5 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <Radio className="w-5 h-5 text-blue-400" />
                      <h3 className="font-medium">Forward Detection Zone</h3>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Scans the area ahead of you, detecting obstacles before you reach 
                      them. The sensor creates an invisible detection field that provides 
                      early warning through gentle feedback.
                    </p>
                  </div>

                  <div className="p-5 rounded-xl bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <Waves className="w-5 h-5 text-purple-400" />
                      <h3 className="font-medium">Surface Detection Zone</h3>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Monitors the ground directly ahead of your next step. Identifies 
                      potholes, drops, curbs, steps, and changes in surface before your 
                      foot reaches them.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Design Philosophy */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">
                Design Philosophy
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                Complete Integration
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                ClearWay is designed as a complete smart mobility stick — not an 
                add-on or attachment. All sensing technology, feedback systems, 
                and connectivity features are seamlessly integrated into a single, 
                cohesive device.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-400 font-medium">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">No External Accessories</h4>
                    <p className="text-gray-500 text-sm">Everything you need is built into the stick itself.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-400 font-medium">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">No Smartphone Required</h4>
                    <p className="text-gray-500 text-sm">ClearWay works independently for obstacle detection.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-400 font-medium">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Natural Form Factor</h4>
                    <p className="text-gray-500 text-sm">Designed to feel familiar and comfortable to use.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent border border-gray-800/50">
                {/* Interactive SVG instead of stock image */}
                <svg viewBox="0 0 400 500" className="w-full h-full p-8">
                  <defs>
                    <linearGradient id="integrationGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity="0.1" />
                    </linearGradient>
                  </defs>
                  
                  {/* Central stick */}
                  <rect x="188" y="80" width="24" height="340" rx="12" fill="#2a2a2a" />
                  
                  {/* Module */}
                  <rect x="165" y="120" width="70" height="90" rx="10" fill="#1a1a1a" stroke="#3a3a3a" strokeWidth="1" />
                  
                  {/* Connection lines showing integration */}
                  <motion.path
                    d="M 200 160 L 100 100"
                    stroke="#3b82f6"
                    strokeWidth="1"
                    strokeDasharray="5 5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <motion.path
                    d="M 200 160 L 300 100"
                    stroke="#a855f7"
                    strokeWidth="1"
                    strokeDasharray="5 5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  />
                  <motion.path
                    d="M 200 180 L 100 250"
                    stroke="#10b981"
                    strokeWidth="1"
                    strokeDasharray="5 5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                  />
                  
                  {/* Feature bubbles */}
                  <g>
                    <circle cx="100" cy="100" r="30" fill="#1a1a1a" stroke="#3b82f6" strokeWidth="1" />
                    <text x="100" y="95" textAnchor="middle" fill="#3b82f6" fontSize="8">FORWARD</text>
                    <text x="100" y="108" textAnchor="middle" fill="#3b82f6" fontSize="8">SENSE</text>
                  </g>
                  <g>
                    <circle cx="300" cy="100" r="30" fill="#1a1a1a" stroke="#a855f7" strokeWidth="1" />
                    <text x="300" y="95" textAnchor="middle" fill="#a855f7" fontSize="8">GROUND</text>
                    <text x="300" y="108" textAnchor="middle" fill="#a855f7" fontSize="8">SCAN</text>
                  </g>
                  <g>
                    <circle cx="100" cy="250" r="30" fill="#1a1a1a" stroke="#10b981" strokeWidth="1" />
                    <text x="100" y="245" textAnchor="middle" fill="#10b981" fontSize="8">HAPTIC</text>
                    <text x="100" y="258" textAnchor="middle" fill="#10b981" fontSize="8">FEEDBACK</text>
                  </g>
                  
                  {/* Pulsing center */}
                  <motion.circle
                    cx="200"
                    cy="165"
                    r="15"
                    fill="#10b981"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </svg>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Engineering Principles</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The technology behind ClearWay follows core principles focused on real-world usability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Reliability First',
                description: 'Every component is selected for dependability. ClearWay is designed to work consistently, day after day, without requiring technical expertise.'
              },
              {
                title: 'Practical Sensing',
                description: 'Sensors are chosen for real-world effectiveness, not novelty. The focus is on detecting the hazards that matter most in everyday navigation.'
              },
              {
                title: 'Simple Operation',
                description: 'No complex setup or calibration. ClearWay is designed to be picked up and used immediately, with intuitive feedback that requires no learning curve.'
              }
            ].map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/30 to-transparent border border-gray-800/50"
              >
                <h3 className="text-xl font-semibold mb-4">{principle.title}</h3>
                <p className="text-gray-400 leading-relaxed">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Learn about Find My integration
            </h2>
            <p className="text-gray-400 mb-8">
              ClearWay works with Apple Find My so you can always locate your stick.
            </p>
            <Link
              to="/find-my"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-all"
            >
              Explore Find My
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyPage;
